import { useEffect, useState } from "react";
import { Plus, Receipt, Lock, Copy, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { toLocalIsoString } from "@/lib/agendaConstants";
import { PAYMENT_METHOD_LABELS, PAYMENT_METHODS } from "@/lib/patientConstants";
import { PATIENTS, PAYMENTS } from "@/constants/testIds";
import SquareChargeModal from "@/components/payments/SquareChargeModal";

const LINK_STATUS_LABELS = { pending: "Pendiente", completed: "Pagado", cancelled: "Cancelado" };
const LINK_STATUS_COLORS = { pending: "bg-[#F4E4D6] text-[#B5793A]", completed: "bg-[#E3EDE1] text-[#5B7A54]", cancelled: "bg-muted text-muted-foreground" };

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

/** Historial comercial (pagos) del paciente. Recepcion no tiene acceso
 * (el backend responde 403; aca directamente no se renderiza el tab). */
export default function PatientPaymentsTab({ patientId }) {
  const [payments, setPayments] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [squareModalOpen, setSquareModalOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "cash", notes: "", paid_at: toLocalIsoString(new Date()) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [restricted, setRestricted] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const [paymentsRes, linksRes] = await Promise.all([
        api.get(`/patients/${patientId}/payments`),
        api.get("/payments/square/links", { params: { patient_id: patientId } }).catch(() => ({ data: [] })),
      ]);
      setPayments(paymentsRes.data);
      setLinks(linksRes.data);
    } catch (err) {
      if (err.response?.status === 403) setRestricted(true);
      else toast({ title: "Error al cargar pagos", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      setError("Ingresa un monto válido");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.post(`/patients/${patientId}/payments`, {
        amount: Number(form.amount),
        method: form.method,
        notes: form.notes || null,
        paid_at: form.paid_at,
      });
      toast({ title: "Pago registrado" });
      setModalOpen(false);
      setForm({ amount: "", method: "cash", notes: "", paid_at: toLocalIsoString(new Date()) });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (restricted) {
    return (
      <div className="bg-white border border-border rounded-xl p-8 text-center max-w-3xl" data-testid={PATIENTS.paymentsRestrictedNotice}>
        <Lock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No tenes permisos para ver la información financiera de este paciente.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">Historial de pagos</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setSquareModalOpen(true)} data-testid={PAYMENTS.patientTabChargeButton}>
            <CreditCard className="w-3.5 h-3.5" /> Cobrar con Square
          </Button>
          <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" onClick={() => setModalOpen(true)} data-testid={PATIENTS.paymentAddButton}>
            <Plus className="w-3.5 h-3.5" /> Registrar pago manual
          </Button>
        </div>
      </div>

      {links.length > 0 && (
        <div className="space-y-2">
          {links.map((l) => (
            <div key={l.id} className="bg-white border border-border rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`${PAYMENTS.linkRowPrefix}${l.id}`}>
              <div className="text-sm">
                <span className="font-medium text-foreground">Link de cobro &middot; ${l.amount.toFixed(2)}</span>
                {l.is_deposit && <span className="text-muted-foreground"> (sena)</span>}
              </div>
              <Badge className={`${LINK_STATUS_COLORS[l.status]} border-none font-normal`}>{LINK_STATUS_LABELS[l.status]}</Badge>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground text-sm">Cargando...</p>
      ) : payments.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid={PATIENTS.paymentsEmptyState}>
          <Receipt className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Todavía no hay pagos registrados.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid={PATIENTS.paymentsList}>
          {payments.map((p) => (
            <div key={p.id} className="bg-white border border-border rounded-xl p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`${PATIENTS.paymentItemPrefix}${p.id}`}>
              <div>
                <p className="text-sm font-medium text-foreground">
                  ${p.amount.toFixed(2)} &middot; {PAYMENT_METHOD_LABELS[p.method]}
                  {p.provider === "square" && <Badge variant="outline" className="ml-2 text-[10px]">Square</Badge>}
                  {p.is_deposit && <span className="text-muted-foreground text-xs"> (sena)</span>}
                </p>
                <p className="text-xs text-muted-foreground">{formatDate(p.paid_at)}{p.notes ? ` \u00b7 ${p.notes}` : ""}</p>
              </div>
              {p.receipt_url && (
                <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-sm text-brand underline" data-testid={`${PAYMENTS.paymentReceiptLinkPrefix}${p.id}`}>Ver recibo</a>
              )}
            </div>
          ))}
        </div>
      )}

      <SquareChargeModal
        open={squareModalOpen}
        onOpenChange={(v) => { setSquareModalOpen(v); if (!v) load(); }}
        patient={{ id: patientId }}
        onCreated={load}
      />

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md" data-testid={PATIENTS.paymentModal}>
          <DialogHeader><DialogTitle className="font-serif text-2xl">Registrar pago</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Monto (USD)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid={PATIENTS.paymentModalAmountInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Método de pago</Label>
              <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}>
                <SelectTrigger data-testid={PATIENTS.paymentModalMethodSelect}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Fecha de pago</Label>
              <Input type="datetime-local" value={form.paid_at} onChange={(e) => setForm((f) => ({ ...f, paid_at: e.target.value }))} data-testid={PATIENTS.paymentModalDateInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid={PATIENTS.paymentModalNotesInput} rows={2} />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid={PATIENTS.paymentModalCancelButton}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid={PATIENTS.paymentModalSaveButton}>{saving ? "Guardando..." : "Registrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
